import type { CloudApi } from './api';
import { createOffAuth, createAuthService, type AuthService } from './auth';
import { parseCloudConfig } from './config';
import { createSupabaseApi } from './supabaseApi';

/**
 * The cloud as the controller receives it: an account service and, when a
 * project is configured, the API for its documents. `@supabase/supabase-js`
 * is loaded only when configuration exists, as its own chunk, so a copy of
 * MyTXT without a backend carries none of it.
 */
export interface Cloud {
  readonly configured: boolean;
  /** Why cloud was left off although variables were present (a privileged key, a malformed URL). */
  readonly problem: string | null;
  readonly auth: AuthService;
  readonly api: CloudApi | null;
}

const REQUEST_TIMEOUT_MS = 30_000;

/** `fetch` that gives up after 30 s; the failure is a lost connection like any other and is retried. */
export const fetchWithTimeout: typeof fetch = (input, init) => {
  if (typeof AbortSignal === 'undefined' || !AbortSignal.timeout) return fetch(input, init);
  const timeout = AbortSignal.timeout(REQUEST_TIMEOUT_MS);
  const signal = init?.signal && AbortSignal.any ? AbortSignal.any([init.signal, timeout]) : (init?.signal ?? timeout);
  return fetch(input, { ...init, signal });
};

export function offCloud(problem: string | null = null): Cloud {
  return { configured: false, problem, auth: createOffAuth(), api: null };
}

export async function createCloud(env: Parameters<typeof parseCloudConfig>[0] = import.meta.env): Promise<Cloud> {
  const result = parseCloudConfig(env);
  if (result.status === 'absent') return offCloud();
  if (result.status === 'invalid') {
    console.error(`MyTXT cloud is off: ${result.reason}`);
    return offCloud(result.reason);
  }
  try {
    const { createClient } = await import('@supabase/supabase-js');
    const client = createClient(result.config.url, result.config.key, {
      // A request that never answers must not hold a document's sync (or the library) hostage.
      global: { fetch: fetchWithTimeout },
      auth: {
        flowType: 'pkce',
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storageKey: 'mytxt.auth',
      },
    });
    return { configured: true, problem: null, auth: createAuthService(client), api: createSupabaseApi(client) };
  } catch (error) {
    console.error(error);
    return offCloud('The cloud client could not be loaded.');
  }
}
